import { FormControlLabel, MenuItem, Stack, Switch, TextField } from '@mui/material';
import PropTypes from 'prop-types';

import { ROW_STATUS } from '../utils/bulkLotNdcStatus';
import { STATUS_LABEL } from '../utils/bulkLotNdcStatusLabels';

export const ALL = 'All';

/**
 * POM-6010 — the review grid's filter row.
 *
 * Every option is derived from the rows currently in the batch rather than fetched: this is a
 * grouping of the selection the user already made, so there is nothing to look up. Only statuses
 * that actually occur are offered, and filtering never touches the uncommitted changes.
 *
 * `trailingAction` is the row's right-hand end, for the Commit control. It rides here rather than
 * in the header so the badge counting edits sits next to the rows those edits were made in; it is
 * a node rather than rendered inline because what it does is the dialog's business, not the filter
 * row's.
 */
const BulkLotNdcReviewFilters = ({
  rows,
  filters,
  onChange,
  search,
  onSearchChange,
  trailingAction,
}) => {
  // Row order is selection order, which reads as no order at all in a dropdown — offer them A–Z.
  const optionsFor = (accessor) =>
    [...new Set(rows.map((row) => accessor(row.item)).filter(Boolean))].sort((left, right) =>
      String(left).localeCompare(String(right), undefined, { sensitivity: 'base' })
    );

  // The status list mirrors what the grid is actually showing. Offering "Recommended" while those
  // rows are hidden is a filter that can only ever empty the grid.
  const statusOptions = [...new Set(rows.map((row) => row.status))].filter(
    (status) => filters.showRecommended || status !== ROW_STATUS.AS_ALLOCATED
  );

  /*
   * Turning the toggle off takes "Recommended" out of that list, so a Status sitting on it would be
   * left holding a value its own select no longer offers — MUI warns, renders the field blank, and
   * the grid can never match a row again. Clearing it back to All belongs in the same change,
   * because this is the only moment both halves are known.
   */
  const handleShowRecommended = (checked) =>
    onChange({
      ...filters,
      showRecommended: checked,
      status: !checked && filters.status === ROW_STATUS.AS_ALLOCATED ? ALL : filters.status,
    });

  const select = (key, label, values, renderLabel) => (
    <TextField
      select
      size='small'
      label={label}
      value={filters[key]}
      onChange={(event) => onChange({ ...filters, [key]: event.target.value })}
      sx={{ minWidth: 150 }}
    >
      <MenuItem value={ALL}>All</MenuItem>
      {values.map((value) => (
        <MenuItem key={value} value={value}>
          {renderLabel ? renderLabel(value) : value}
        </MenuItem>
      ))}
    </TextField>
  );

  /*
   * gap alone, no `spacing`: Stack's spacing prop injects a margin-left on every child after the
   * first, through a descendant selector that outranks a child's own sx. That silently beat the
   * `ml: 'auto'` that is supposed to push trailingAction to the end of the row. gap does the same
   * spacing job, wraps properly, and leaves the child's margins alone.
   */
  return (
    <Stack direction='row' sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
      {select(
        'drug',
        'Drug',
        optionsFor((item) => item.medicationName)
      )}
      {select(
        'facility',
        'Facility',
        optionsFor((item) => item.fulfillingPharmacyName)
      )}
      {select(
        'partner',
        'Partner',
        optionsFor((item) => item.partner)
      )}
      {select('status', 'Status', statusOptions, (value) => STATUS_LABEL[value] ?? value)}
      <TextField
        size='small'
        placeholder='Search order id or Rx number'
        value={search}
        onChange={(event) => onSearchChange(event.target.value)}
        sx={{ minWidth: 220 }}
      />
      {/* Off on every open: a Recommended row has been checked and needs no decision, so the rows
          that do need one are what the grid opens on. It is a display filter and nothing more —
          the batch that prints is the same on both sides of it. */}
      <FormControlLabel
        control={
          <Switch
            size='small'
            checked={!!filters.showRecommended}
            onChange={(event) => handleShowRecommended(event.target.checked)}
          />
        }
        label='Show Recommended Lots'
        // ml: 0 undoes FormControlLabel's own -11px, which pulled the switch out of the row.
        sx={{ ml: 0, mr: 0.5, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
      />
      {trailingAction}
    </Stack>
  );
};

BulkLotNdcReviewFilters.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  filters: PropTypes.shape({
    drug: PropTypes.string,
    facility: PropTypes.string,
    partner: PropTypes.string,
    status: PropTypes.string,
    showRecommended: PropTypes.bool,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  search: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  trailingAction: PropTypes.node,
};

BulkLotNdcReviewFilters.defaultProps = {
  trailingAction: null,
};

export default BulkLotNdcReviewFilters;
